<template>
  <div id="settings-engineers-audit-report">
    <div
      v-if="hasPermission"
      class="float-right"
    >
      <button
        type="button"
        class="btn btn--orange"
        :disabled="allOptionsChecked.bDisableExportBtn"
        @click="exportData()"
      >{{ $t('action.export') }}</button>
    </div>
    <label class="emphasis audit-report-header">{{ $t('settings.auditReportExport.title') }}</label>
    <div class="check-type">
      <input
        id="settings-engineers-audit-report-all"
        type="checkbox"
        :checked="allOptionsChecked.bMarkAllCheckBox"
        @change="event => { setAllOptionChecked(event.target.checked) }"
      >
      <label
        for="settings-engineers-audit-report-all"
        class="audit-report-label"
      >
        {{ $t('settings.auditReportExport.all') }}
      </label>
    </div>
    <div
      v-for="item in exportOptions"
      :key="item.key"
      class="check-type"
    >
      <input
        :id="`settings-engineers-audit-report-${item.key}`"
        type="checkbox"
        :checked="item.checked"
        @change="event => item.checked = event.target.checked"
      >
      <label
        :for="`settings-engineers-audit-report-${item.key}`"
        class="audit-report-label"
      >
        {{ $t(item.name) }}
      </label>
    </div>
  </div>
</template>

<script>
import { computed, onBeforeMount, ref } from 'vue-demi'
import { downloadAllFilesAsZip } from '@/api/events/request'
import { useStoreGetters } from '@/store/index'
import { aclHelper } from '@/utils/aclHelper'
import i18n from '@/utils/i18n'

export default {
  name: 'AuditReport',
  setup () {
    const { enableTenderFeature, enableProjectFeature, enableTenancyRecordsFeature, enableTenantProjectsFeature, enableWorkPermitsFeature } = useStoreGetters('regions', [
      'enableTenderFeature',
      'enableProjectFeature',
      'enableTenancyRecordsFeature',
      'enableTenantProjectsFeature',
      'enableWorkPermitsFeature',
    ])
    const exportOptions = ref([])
    const initOpts = () => {
      const defaultOptions = [{
        key: 'hklUsers',
        name: 'settings.auditReportExport.hklUsers',
        url: '/auditReports/hklUser',
        enabled: true,
        checked: false,
      }, {
        key: 'tenancyUsers',
        name: 'settings.auditReportExport.tenancyUsers',
        url: '/auditReports/tenancyUser',
        enabled: enableTenancyRecordsFeature.value,
        checked: false,
      }, {
        key: 'tenantContractorUsers',
        name: 'settings.auditReportExport.tenantContractorUsers',
        url: '/auditReports/tenantProjectContractor',
        enabled: enableTenantProjectsFeature.value && enableWorkPermitsFeature.value,
        checked: false,
      }, {
        key: 'vendorUsers',
        name: 'settings.auditReportExport.vendorUsers',
        url: '/auditReports/vendorUser',
        enabled: enableTenderFeature.value || enableProjectFeature.value,
        checked: false,
      }, {
        key: 'vendorContractorUsers',
        name: 'settings.auditReportExport.vendorContractorUsers',
        url: '/auditReports/vendorContractorUsers',
        enabled: enableProjectFeature.value && enableWorkPermitsFeature.value,
        checked: false,
      }, {
        key: 'tmsRepUsers',
        name: 'settings.auditReportExport.tmsRepresentativeUsers',
        url: '/auditReports/tmsRepUsers',
        enabled: enableTenancyRecordsFeature.value,
        checked: false,
      }, {
        key: 'representativeUsers',
        name: 'settings.auditReportExport.representativeUsers',
        url: '/auditReports/representativeUsers',
        enabled: enableTenderFeature.value,
        checked: false,
      }, {
        key: 'securityGroup',
        name: 'settings.auditReportExport.securityGroup',
        url: '/auditReports/securityGroup',
        enabled: true,
        checked: false,
      }]

      exportOptions.value = defaultOptions.filter(item => item.enabled)
    }

    const hasPermission = computed(() => {
      return aclHelper.getSettingsUpdateAccess() || aclHelper.hasSettingsReadAccess()
    })

    const exportData = async () => {
      const files = exportOptions.value.map(file => ({
        ...file,
        name: i18n.t(file.name),
      }))
      await downloadAllFilesAsZip(files, 'AuditReports.zip', i18n.t('settings.auditReportExport.exportFailed'))
    }

    const setAllOptionChecked = (bool = true) => {
      for (const option in exportOptions.value) {
        exportOptions.value[option].checked = bool
      }
    }

    const allOptionsChecked = computed(() => {
      const options = Object.values(exportOptions.value).map(option => option.checked)
      return {
        bMarkAllCheckBox: !options.includes(false),
        bDisableExportBtn: !options.includes(true),
      }
    })
    onBeforeMount(() => {
      initOpts()
    })
    return {
      exportOptions,
      hasPermission,
      allOptionsChecked,
      exportData,
      setAllOptionChecked,
    }
  },
}
</script>

<style lang="scss" scoped>
@import '~styles/settings/style';
#settings-engineers-audit-report {
  padding: 0 24px 15px 24px;
  input:focus {
    outline: none;
  }
}
.audit-report-header {
  font-size: 16px;
  font-weight: bold;
  padding: 10px 0;
}
.audit-report-label {
  font-weight: normal;
  margin-top: 5px;
}
</style>
